import { axios } from '@/utils'

const STORAGE_VERSION = 1
const STORAGE_KEY = 'auth_data'

/**
 * Centralized token management utility
 * Handles localStorage persistence and axios header configuration
 */
export class TokenManager {
    /**
     * Save authentication tokens to localStorage
     * @param {string} token - Auth token
     * @param {string} tokenType - 'Token' or 'Bearer'
     * @param {boolean} remember - Whether to persist to localStorage
     */
    static save(token, tokenType, remember = true) {
        if (!remember) return

        const data = {
            version: STORAGE_VERSION,
            token,
            tokenType,
            timestamp: Date.now()
        }

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data))

            // Also save in legacy format for backward compatibility with src/utils/axios.js
            // This ensures tokens are loaded on page refresh
            localStorage.setItem('token', token)
            localStorage.setItem('token_type', tokenType)
        } catch (error) {
            console.error('Failed to save auth data to localStorage:', error)
        }
    }

    /**
     * Load authentication tokens from localStorage
     * @returns {Object|null} {token, tokenType} or null if not found/invalid
     */
    static load() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (!raw) return null

            const data = JSON.parse(raw)

            // Version check - clear if version mismatch
            if (data.version !== STORAGE_VERSION) {
                this.clear()
                return null
            }

            return {
                token: data.token,
                tokenType: data.tokenType
            }
        } catch (error) {
            console.error('Failed to load auth data from localStorage:', error)
            this.clear()
            return null
        }
    }

    /**
     * Clear all authentication data from localStorage
     * Includes both new format and legacy keys for backward compatibility
     */
    static clear() {
        // New format
        localStorage.removeItem(STORAGE_KEY)

        // Legacy format (for backward compatibility during migration)
        localStorage.removeItem('token')
        localStorage.removeItem('token_type')
        localStorage.removeItem('user')
        localStorage.removeItem('eventOwnerId')

        // Session storage cleanup
        sessionStorage.removeItem('recover_picacode')
        sessionStorage.removeItem('auto-retry-checkin')
    }

    /**
     * Set axios default Authorization header
     * @param {string} token - Auth token
     * @param {string} tokenType - 'Token' or 'Bearer'
     */
    static setAxiosHeader(token, tokenType) {
        if (!token || !tokenType) {
            console.warn('Attempted to set axios header with empty token or tokenType')
            return
        }

        axios.defaults.headers.common['Authorization'] = `${tokenType} ${token}`
    }

    /**
     * Clear axios Authorization header
     */
    static clearAxiosHeader() {
        delete axios.defaults.headers.common['Authorization']
    }

    /**
     * Save user data to localStorage
     * @param {Object} user - User object
     * @param {boolean} remember - Whether to persist
     */
    static saveUser(user, remember = true) {
        if (!remember) return

        try {
            localStorage.setItem('user', JSON.stringify(user))
        } catch (error) {
            console.error('Failed to save user to localStorage:', error)
        }
    }

    /**
     * Load user data from localStorage
     * @returns {Object|null} User object or null
     */
    static loadUser() {
        try {
            const raw = localStorage.getItem('user')
            return raw ? JSON.parse(raw) : null
        } catch (error) {
            console.error('Failed to load user from localStorage:', error)
            return null
        }
    }
}

export default TokenManager
